// @flow
import shallowEqual from 'mz-utils/shallowEqual'
import normalizeErrors from 'mz-sdk/utils/normalizeErrors'
import debounce from 'mz-utils/debounce'
import maybeForEach from 'mz-utils/maybeForEach'
import is from 'mz-utils/is'
import { lockAllFields, unlockAllFields, calculateDeepMeta } from './utils'
import messages from 'mz-sdk/messages.intl'

// Types
import type { Meta } from '../../core/BaseField'

export type NestOptions = {
  dependsOnValue?: boolean,
  dependsOnMeta?: boolean,
  emptyOnDestroy?: boolean,
  emptyValue?: any,
}
export type Constructor = {
  name?: string,
  initValue?: any,
  initMeta?: Meta,
  emptyValue?: any,
  valueSetter?: Function,
  valueGetter?: Function,
  metaSetter?: Function,
  metaGetter?: Function,
  destroyFromParent?: Function,
  onUpdateState?: Function,
}
export type ControllerUpdate = {
  view?: boolean,
  meta?: boolean,
  value?: boolean,
  updatedOutside?: boolean,
}
export type SubscribeOptions = {
  validate?: FieldProcessor,
}
export type WatcherObj = {
  watcher: (update: ControllerUpdate) => any,
  options: ?SubscribeOptions,
}
export type FieldProcessor = (value: any, meta: Meta) => any

// Utils
const VALID_META_FIELDS = {
  deepValid: true,
  valid: true,
  warning: '',
  error: '',
  info: '',
}

const EMPTY_META: Meta = {
  ...VALID_META_FIELDS,
  loading: false,
  active: false,
  touched: false,
  locked: false,
  deepLoading: false,
  deepActive: false,
  deepTouched: false,
}

/**
 * Controller class that holds the whole form (field with nested fields) state
 * and triggers watchers on value/meta updates.
 * Can also validate and normalize the value.
 */
class FieldController {
  iteration = 0

  name = ''

  children = {}

  fieldDomNode: ?Element = null

  watchers: Array<WatcherObj> = []

  internalMeta: Meta

  internalValue: any

  emptyValue: any

  destroyFromParent: Function

  valueSetter: Function

  valueGetter: Function

  metaSetter: Function

  metaGetter: Function

  onUpdateState: Function

  constructor(options: Constructor = {}) {
    this.name = options.name
    this.emptyValue = is.undef(options.emptyValue) ? null : options.emptyValue
    this.internalValue = options.initValue
    this.onUpdateState = options.onUpdateState || (() => {})
    this.internalMeta = !this.metaGetter
      ? { ...EMPTY_META, ...options.initMeta }
      : {}
    this.destroyFromParent = options.destroyFromParent || (() => {})
    this.valueGetter = options.valueGetter || (() => this.internalValue)
    this.metaGetter = options.metaGetter || (() => this.internalMeta)
    this.valueSetter =
      options.valueSetter ||
      ((value) => {
        this.internalValue = value
      })
    this.metaSetter =
      options.metaSetter ||
      ((meta) => {
        this.internalMeta = meta
      })
  }

  get value(): ?any {
    return this.valueGetter()
  }

  get meta(): Meta {
    return this.metaGetter()
  }

  setFieldDOMNode(node: ?Element) {
    this.fieldDomNode = node
  }

  subscribe(watcher: Function, options?: SubscribeOptions) {
    this.watchers.push({ watcher, options })
    if (options && options.validate) {
      this.validate()
    }
  }

  unsubscribe(watcher: Function) {
    this.watchers = this.watchers.filter((x) => x.watcher !== watcher)
    if (!this.watchers.length) {
      this.destroyFromParent()
    }
  }

  handleStateUpdate(updatedObj: ControllerUpdate) {
    maybeForEach(this.watchers, (f) => f.watcher(updatedObj))
    this.onUpdateState(updatedObj)

    if (updatedObj.value) {
      this.validate()
    }
  }

  runFieldProcessor(
    processor: FieldProcessor,
    handler: Function
  ): Promise<any> {
    const currValue = this.value
    const currMeta = this.metaGetter()
    let result = processor(currValue, currMeta)

    if (is.promise(result)) {
      this.setMeta({
        ...currMeta,
        ...VALID_META_FIELDS,
        valid: false,
        loading: true,
      })
      result = result.then(handler).catch((error) => {
        if (!error) {
          handler(messages.UNKNOWN_ERROR)
        } else handler(error)
      })
    } else if (
      (is.array(result) && result.length) ||
      (!is.array(result) && result) ||
      !currMeta.valid ||
      currMeta.error ||
      currMeta.info ||
      currMeta.warning ||
      currMeta.loading
    ) {
      handler(result)
    }

    return Promise.resolve(result)
  }

  getDeepValidations = (controller: FieldController = this) => {
    let validations = [controller.validateInstantly()]
    controller.iterateChildren((childController) => {
      const childValidations = controller.getDeepValidations(childController)
      validations = validations.concat(childValidations)
    })
    return validations
  }

  deepValidate = () => {
    const validations = this.getDeepValidations()
    return Promise.all(validations)
  }

  submit = (submitter: FieldProcessor) => {
    this.setMeta(lockAllFields(this.meta))
    const unlocker = () => this.setMeta(unlockAllFields(this.meta))

    return this.runFieldProcessor(submitter, this.handleValidationResult)
      .then(unlocker)
      .catch(unlocker)
  }

  validateInstantly = (): Promise<any> => {
    if (this.meta.locked) return Promise.resolve(null)

    const requestIteration = ++this.iteration
    const validationHandler = (result) =>
      !this.meta.locked &&
      requestIteration === this.iteration &&
      this.handleValidationResult(result)

    return this.runFieldProcessor(this.doValidateField, validationHandler)
  }

  validate = debounce(
    {
      wait: 120,
      leading: true,
      trailing: true,
    },
    this.validateInstantly
  )

  doValidateField = (value: any, meta: Meta): any => {
    const result = this.watchers
      .map((w) => {
        const validator = w.options && w.options.validate
        return validator && validator(value, meta)
      })
      .filter((x) => x)

    const havePromise = result.some(is.promise)
    return havePromise ? Promise.all(result) : result
  }

  handleValidationResult = (results: any) => {
    const validationResult = normalizeErrors(results)

    this.setMeta({
      ...this.meta,
      loading: false,
      valid: !validationResult.errors.length,
      error: validationResult.errors[validationResult.errors.length - 1] || '',
      warning:
        validationResult.warnings[validationResult.warnings.length - 1] || '',
      info: validationResult.info[validationResult.info.length - 1] || '',
    })
  }

  getOrCreateChild(name: string, options?: NestOptions): FieldController {
    if (!this.children[name]) {
      this.children[name] = this.createChild(name, options)
    }
    return this.children[name]
  }

  createChild(name: string, options?: NestOptions = {}): FieldController {
    const valueGetter = () => {
      const value = this.valueGetter()
      return value && value[name]
    }

    const valueSetter = (childValue: any) => {
      const value = this.valueGetter()
      const newValue = {
        ...value,
        [name]: childValue,
      }

      this.valueSetter(newValue)
    }

    const metaGetter = () => {
      const meta = this.metaGetter()
      return (meta.children && meta.children[name]) || { ...EMPTY_META }
    }

    const metaSetter = (childMeta: ?Meta) => {
      const oldMeta = this.metaGetter()
      const newMeta = calculateDeepMeta({
        ...oldMeta,
        children: {
          ...oldMeta.children,
          [name]: childMeta,
        },
      })

      this.metaSetter(newMeta)
    }

    const destroyFromParent = () => {
      const childController = this.children[name]
      const { meta } = this

      delete this.children[name]
      meta.children && delete meta.children[name]

      this.setMeta(meta)
      if (options.emptyOnDestroy) {
        valueSetter(childController.emptyValue)
        childController.handleStateUpdate({ value: true })
      }
    }

    const handleUpdateState = (updateOptions: ControllerUpdate) => {
      this.handleStateUpdate({
        ...updateOptions,
        view:
          (updateOptions.value && options.dependsOnValue) ||
          (updateOptions.meta && options.dependsOnMeta),
      })
    }

    const { meta } = this
    meta.children = meta.children || {}
    meta.children[name] = {
      ...EMPTY_META,
      ...meta.children[name],
    }

    return new FieldController({
      name,
      valueSetter,
      valueGetter,
      metaSetter,
      metaGetter,
      destroyFromParent,
      emptyValue: options.emptyValue,
      onUpdateState: handleUpdateState,
    })
  }

  iterateChildren(iterator: Function) {
    const childrenKeys = Object.keys(this.children)
    maybeForEach(childrenKeys, (k) => iterator(this.children[k], k))
  }

  setMeta = (newMeta: Meta, updateOptions?: ControllerUpdate): boolean => {
    this.iterateChildren((childController, k) => {
      const childMeta = newMeta && newMeta.children && newMeta.children[k]
      childController.setMeta(childMeta, updateOptions)
    })

    const updatedMeta = calculateDeepMeta({
      ...newMeta,
      children: this.meta.children || {},
    })

    if (
      !shallowEqual(updatedMeta, this.meta, { exclude: { children: true } })
    ) {
      this.metaSetter(updatedMeta, updateOptions)
      this.handleStateUpdate({ meta: true, view: true, ...updateOptions })
      return true
    }

    return false
  }

  setValue = (newValue: ?any, updateOptions?: ControllerUpdate): boolean => {
    if (this.meta.locked) return false

    let hasUpdatedChildren = false
    this.iterateChildren((childController, k) => {
      const childValue = newValue ? newValue[k] : undefined
      hasUpdatedChildren =
        childController.setValue(childValue, updateOptions) ||
        hasUpdatedChildren
    })

    if (hasUpdatedChildren || !shallowEqual(newValue, this.value)) {
      this.valueSetter(newValue, updateOptions)
      this.handleStateUpdate({ value: true, view: true, ...updateOptions })
      return true
    }

    return false
  }
}

export default FieldController
